import { useLife } from '@/state/life';
import { usePower } from '@/state/power';
import { useXClone } from '@/state/xclone';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * TikTok Appearance mini-arc.
 *
 * 3 fast decisions about how cringe to lean. Outcome: a viral "moment"
 * (good or catastrophic) that drops as a CinematicTakeover and rewires
 * tribal rep based on choices.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => number }[]; // returns "cringe score" delta
}

const BEATS: Beat[] = [
  {
    text: 'TIKTOK MANAGER: "first video — what\'s the format?"',
    options: [
      { label: 'corny CEO doing a trending dance', effect: () => { usePower.getState().bumpRep('wsb', 8); useLife.getState().bumpSanity(-3); return 2; } },
      { label: 'serious "day in the life of a billionaire" voice-over', effect: () => { usePower.getState().bumpRep('cnbc', 6); return 0; } },
      { label: 'a 17-second rant about the SEC', effect: () => { usePower.getState().bumpRep('crypto', 10); useLife.getState().bumpSecHeat(8); return 1; } },
    ],
  },
  {
    text: 'COMMENTS section: half are calling you a fraud, half want to marry you. respond?',
    options: [
      { label: 'ignore', effect: () => { return 0; } },
      { label: 'reply to one mean comment with "stay poor"', effect: () => { usePower.getState().bumpNotoriety(6); usePower.getState().bumpRep('press', -4); return 2; } },
      { label: 'pin a comment from a 14-year-old asking for stock tips', effect: () => { usePower.getState().bumpRep('wsb', 6); useLife.getState().bumpSecHeat(5); return 2; } },
    ],
  },
  {
    text: 'TIKTOK ALGORITHM offers you a brand deal. gummy vitamins. \$30K for one video. take it?',
    options: [
      { label: 'take the deal. shill the gummies.', effect: () => { useLife.getState().bumpSanity(-2); usePower.getState().bumpRep('cnbc', -4); return 2; } },
      { label: 'demand \$300K. they say no. you tweet about integrity.', effect: () => { usePower.getState().bumpRep('press', 5); return 0; } },
      { label: 'partner with a competitor of theirs and rage-bait the original brand', effect: () => { usePower.getState().bumpNotoriety(15); usePower.getState().bumpRep('wsb', 8); return 3; } },
    ],
  },
];

let cringeScore = 0;

export function startTikTokArc(): void {
  cringeScore = 0;
  useMeta.getState().recordLore('arc-tiktok-debut', 'TikTok Debut');
  useCinematic.getState().fire({
    id: `tiktok-${Date.now()}`,
    type: 'breaking',
    headline: 'TIKTOK CALLS.',
    subhead: 'your manager wants you doing 3 vertical videos this week. you can be authentic. or rich.',
    durationMs: 4500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `tiktok-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `tiktok-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          cringeScore += opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'broker',
        fromNpc: 'broker',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `tiktok: beat ${i + 1}/3` });
      sounds.notifyAlert();
    }, 4_000 + i * 25_000);
  }

  setTimeout(() => {
    if (cringeScore >= 5) {
      useCinematic.getState().fire({
        id: `tiktok-viral-${Date.now()}`,
        type: 'breaking',
        headline: 'YOU WENT VIRAL.',
        subhead: '14M views in 6 hours. 7 brand deals in your DMs. 3 separate think-pieces calling you "the face of decline."',
        durationMs: 5500,
      });
      usePower.getState().bumpNotoriety(25);
      usePower.getState().bumpRep('wsb', 18);
      useXClone.getState().receivePost({
        authorHandle: 'TikTokTopBrass', authorTribe: 'press',
        text: 'a "well-known retail trader" has somehow accumulated 14M views with 47 seconds of footage. our analyst: "i can\'t even describe it. you have to watch it."',
        likes: 28000, reposts: 8400,
      });
    } else {
      useCinematic.getState().fire({
        id: `tiktok-flop-${Date.now()}`,
        type: 'breaking',
        headline: 'YOUR VIDEO FLOPPED.',
        subhead: '320 views. one of which was your mom. she sent screenshots to family group chat. the rest of the family is "concerned."',
        durationMs: 5000,
      });
      useLife.getState().bumpSanity(-5);
      useLife.getState().bumpRelationship('mom', -3);
    }
  }, 4_000 + BEATS.length * 25_000 + 5_000);
}
